import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Select,
  useToast,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { BuyerLegalType, useUpdateOrganizationMutation } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../common/redux/reducers/authStateReducer";

interface FormValues {
  buyerLegalType: BuyerLegalType | "";
}

interface Props {
  currentValue: BuyerLegalType | null;
}

export const ChangeBuyerLegalType = ({ currentValue }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const [editOrg, { isLoading }] = useUpdateOrganizationMutation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const displayer = useApiError();
  const { register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { buyerLegalType: currentValue ?? "" },
  });

  const selected = watch("buyerLegalType");

  const onSubmit = async (values: FormValues) => {
    const result = await editOrg({
      orgId: authState.selectedOrg.id,
      editOrganizationRequest: { buyerLegalType: { value: values.buyerLegalType || null } },
    });
    if ("data" in result) {
      dispatch(updateSelectedOrg(result.data));
      toast({ title: t("Buyer legal type has been updated!"), status: "success" });
    } else {
      displayer.trigger(result.error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl id="buyerLegalType" mt="20px">
        <FormLabel>{t("Buyer legal type")}</FormLabel>
        <Select placeholder={t("Select the type of body you are") ?? ""} {...register("buyerLegalType")}>
          {Object.values(BuyerLegalType).map((type) => (
            <option key={type} value={type}>
              {t(type)}
            </option>
          ))}
        </Select>
        <FormHelperText>
          {t(
            "Decides whether you procure under the public procurement rules, and with that the wording used throughout. For public bodies it is also announced as BT-11 on Doffin and sets which EEA threshold applies — central government authorities have a lower one for supplies and services",
          )}
        </FormHelperText>
      </FormControl>
      {(currentValue ?? "") !== selected && (
        <Button
          mt="10px"
          leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
          variant="solid"
          colorScheme="teal"
          size="sm"
          type="submit"
          isLoading={isLoading}
        >
          {t("Save change")}
        </Button>
      )}
    </form>
  );
};
